import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import AxiosService from "../../services/AxiosHelper";

const baseURL = process.env.REACT_APP_BASEURL;

export const getConversations = createAsyncThunk(
  "communication/getConversations",
  async (filters, { rejectWithValue }) => {
    try {
      const { channel = 'all', status = 'open', search = '', page = 1, limit = 10, businessId = '' } = filters;
      let queryUrl = baseURL + `/v1/communications?page=${page}&limit=${limit}&channel=${channel}&status=${status}&search=${search}&businessId=${businessId}`;
      
      const response = await AxiosService.AxiostRequest({
        url: queryUrl,
        method: "get",
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch conversations");
    }
  }
);

export const getConversationById = createAsyncThunk(
  "communication/getConversationById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/v1/communications/${id}`,
        method: "get",
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch conversation");
    }
  }
);

export const composeEmail = createAsyncThunk(
  "communication/composeEmail",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/v1/communications/email/compose`,
        method: "post",
        data
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to send email");
    }
  }
);

export const replyToConversation = createAsyncThunk(
  "communication/replyToConversation",
  async ({ id, content }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/v1/communications/${id}/reply`,
        method: "post",
        data: { content }
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to send reply");
    }
  }
);

export const initiateWhatsApp = createAsyncThunk(
  "communication/initiateWhatsApp",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/v1/communications/whatsapp/initiate`,
        method: "post",
        data
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to initiate WhatsApp");
    }
  }
);

export const updateConversationStatus = createAsyncThunk(
  "communication/updateConversationStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/v1/communications/${id}/status`,
        method: "patch",
        data: { status }
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to update status");
    }
  }
);

export const getStats = createAsyncThunk(
  "communication/getStats",
  async (businessId = '', { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/v1/communications/stats?businessId=${businessId}`,
        method: "get",
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch stats");
    }
  }
);

const communicationSlice = createSlice({
  name: "communication",
  initialState: {
    conversations: [],
    currentConversation: null,
    loading: false,
    sendingMessage: false,
    stats: { unreadConversations: 0, pendingInvitations: 0, openConversations: 0 },
    pagination: { page: 1, pages: 1, total: 0 },
    filters: { channel: 'all', status: 'open', search: '' },
    error: null
  },
  reducers: {
    setFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getConversations.fulfilled, (state, action) => {
      state.conversations = action.payload.conversations;
      state.pagination = action.payload.pagination;
    });
    builder.addCase(getConversationById.fulfilled, (state, action) => {
      state.currentConversation = action.payload;
      // Update the conversation in the list if it exists
      const index = state.conversations.findIndex(c => c._id === action.payload._id);
      if (index !== -1) {
        state.conversations[index] = action.payload;
      }
    });
    builder.addCase(composeEmail.fulfilled, (state, action) => {
      state.currentConversation = action.payload;
      // Refresh list or add to list
      const index = state.conversations.findIndex(c => c._id === action.payload._id);
      if (index !== -1) {
        state.conversations[index] = action.payload;
      } else {
        state.conversations.unshift(action.payload);
      }
      toast.success("Email sent successfully");
    });
    builder.addCase(replyToConversation.fulfilled, (state, action) => {
      state.currentConversation = action.payload;
      const index = state.conversations.findIndex(c => c._id === action.payload._id);
      if (index !== -1) {
        state.conversations[index] = action.payload;
      }
    });
    builder.addCase(updateConversationStatus.fulfilled, (state, action) => {
      state.currentConversation = action.payload;
      const index = state.conversations.findIndex(c => c._id === action.payload._id);
      if (index !== -1) {
        state.conversations[index] = action.payload;
      }
      toast.success("Conversation updated");
    });
    builder.addCase(getStats.fulfilled, (state, action) => {
      state.stats = action.payload;
    });

    builder.addMatcher(
      (action) => action.type.startsWith("communication/") && action.type.endsWith("/pending"),
      (state, action) => {
        if (action.type.includes("composeEmail") || action.type.includes("replyToConversation")) {
          state.sendingMessage = true;
        } else {
          state.loading = true;
        }
      }
    );
    builder.addMatcher(
      (action) => action.type.startsWith("communication/") && (action.type.endsWith("/fulfilled") || action.type.endsWith("/rejected")),
      (state, action) => {
        state.loading = false;
        state.sendingMessage = false;
        if (action.type.endsWith("/rejected")) {
          state.error = action.payload;
          toast.error(action.payload?.message || "Operation failed");
        }
      }
    );
  },
});

export const { setFilter } = communicationSlice.actions;
export default communicationSlice.reducer;
